import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Dropdown from "./Button/Dropdown";
import { getLocaleString } from "../utils/localization/locale";
import { setuxvalue } from "../handlers/ux";

/**
 * Reusable ColumnsDropdown component for table column visibility management
 *
 * @param {Object} props
 * @param {Object} props.visibleColumns - Object with column names as keys and boolean visibility as values
 * @param {Function} props.onColumnsChange - Callback function when columns change (receives updated columns object)
 * @param {string} props.storageKey - Key for persisting column preferences (e.g., 'DISPUTE_LIST_COLUMNS')
 * @param {boolean} props.isDisabled - Whether the dropdown is disabled
 * @param {Object} props.buttonProps - Additional props for the button
 * @param {string} props.dropdownClassName - Additional CSS classes for dropdown wrapper
 * @param {Object} props.dropdownProps - Additional props for dropdown wrapper
 * @param {string} props.wrapperClassName - Additional CSS classes for outer wrapper
 * @param {Object} props.wrapperStyle - Additional inline styles for outer wrapper
 * @param {boolean} props.showDropdown - Controlled dropdown visibility (optional)
 * @param {Function} props.setShowDropdown - Callback to control dropdown visibility (optional)
 * @param {string} props.checkboxDataCy - data-cy attribute for checkboxes (for Cypress tests)
 * @param {Function} props.renderColumnName - Optional function to customize column name display (receives columnName, returns string)
 * @param {Function} props.filterColumns - Optional function to filter which columns to show (receives columnName, returns boolean)
 */
const ColumnsDropdown = ({
  visibleColumns = {},
  onColumnsChange = () => {},
  storageKey = null,
  isDisabled = false,
  buttonProps = {},
  dropdownClassName = "",
  dropdownProps = {},
  wrapperClassName = "",
  wrapperStyle = {},
  showDropdown: controlledShowDropdown,
  setShowDropdown: controlledSetShowDropdown,
  checkboxDataCy = null,
  renderColumnName = null,
  filterColumns = null,
}) => {
  // Internal state for uncontrolled mode
  const [internalShowDropdown, setInternalShowDropdown] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const showDropdown =
    controlledShowDropdown !== undefined
      ? controlledShowDropdown
      : internalShowDropdown;

  const setShowDropdown =
    controlledSetShowDropdown !== undefined
      ? controlledSetShowDropdown
      : setInternalShowDropdown;

  // Calculate count of visible columns
  const columnsCount = useMemo(
    () => Object.values(visibleColumns).filter(Boolean).length,
    [visibleColumns]
  );

  // Handle column toggle
  const handleColumnToggle = (columnName) => {
    // Prevent unchecking the last visible column
    if (columnsCount === 1 && visibleColumns[columnName]) {
      return;
    }

    const updatedColumns = {
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName],
    };

    // Call the onChange callback
    onColumnsChange(updatedColumns);

    // Persist to storage if storageKey is provided
    if (storageKey) {
      setuxvalue(storageKey, updatedColumns);
    }
  };

  // Handle checkbox change (for direct checkbox clicks)
  const handleCheckboxChange = (e, columnName) => {
    e.stopPropagation();

    // Prevent unchecking the last visible column
    if (columnsCount === 1 && visibleColumns[columnName]) {
      return;
    }

    const updatedColumns = {
      ...visibleColumns,
      [columnName]: e.target.checked,
    };

    onColumnsChange(updatedColumns);

    if (storageKey) {
      setuxvalue(storageKey, updatedColumns);
    }
  };

  // Merge default button props with provided ones
  const mergedButtonProps = {
    as: "span",
    role: "button",
    "data-toggle": "dropdown",
    "aria-haspopup": "true",
    "aria-expanded": "false",
    "data-cy": "columns-dropdown",
    style: { margin: 5 },
    ...buttonProps,
  };

  // Merge default dropdown props with provided ones
  const mergedDropdownProps = {
    id: "companylist",
    style: {
      maxHeight: "50vh",
      overflowY: "auto",
      minWidth: "13rem",
    },
    ...dropdownProps,
  };

  // Merge default dropdown wrapper classes
  const mergedDropdownClassName = `p-0 ${dropdownClassName}`.trim();

  // Merge default wrapper classes
  const mergedWrapperClassName =
    `dropdown d-inline-block ${wrapperClassName}`.trim();

  return (
    <span className={mergedWrapperClassName} style={wrapperStyle}>
      <Dropdown
        showDropdown={showDropdown}
        buttonProps={mergedButtonProps}
        btnClassName="ms-2"
        setShowDropdown={setShowDropdown}
        buttonText={getLocaleString("common.columns", "Columns")}
        dropdownWrapperClassName={mergedDropdownClassName}
        dropdownWrapperProps={mergedDropdownProps}
        columnsButton
      >
        {Object.keys(visibleColumns)
          .filter((columnName) =>
            filterColumns ? filterColumns(columnName) : true
          )
          .map((columnName) => {
            const isChecked = visibleColumns[columnName];
            const isLastColumn = columnsCount === 1;

            return (
              <span
                key={columnName}
                className={`cursor-pointer ${isChecked ? "bg-option-selected-grey" : ""}`}
                onClick={() => handleColumnToggle(columnName)}
              >
                <input
                  type="checkbox"
                  className="me-2 d-inline-block align-middle cursor-pointer"
                  name={columnName}
                  checked={isChecked}
                  disabled={isDisabled || (isLastColumn && isChecked)}
                  data-tlabel={`checkbox ${columnName}`}
                  data-cy={checkboxDataCy || undefined}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleCheckboxChange(e, columnName)}
                />
                <span className="d-inline-block align-middle p-0 w-80">
                  {renderColumnName ? renderColumnName(columnName) : columnName}
                </span>
              </span>
            );
          })}
      </Dropdown>
    </span>
  );
};

ColumnsDropdown.propTypes = {
  visibleColumns: PropTypes.objectOf(PropTypes.bool),
  onColumnsChange: PropTypes.func,
  storageKey: PropTypes.string,
  isDisabled: PropTypes.bool,
  buttonProps: PropTypes.object,
  dropdownClassName: PropTypes.string,
  dropdownProps: PropTypes.object,
  wrapperClassName: PropTypes.string,
  wrapperStyle: PropTypes.object,
  showDropdown: PropTypes.bool,
  setShowDropdown: PropTypes.func,
  checkboxDataCy: PropTypes.string,
  renderColumnName: PropTypes.func,
  filterColumns: PropTypes.func,
};

ColumnsDropdown.defaultProps = {
  visibleColumns: {},
  onColumnsChange: () => {},
  storageKey: null,
  isDisabled: false,
  buttonProps: {},
  dropdownClassName: "",
  dropdownProps: {},
  wrapperClassName: "",
  wrapperStyle: {},
  showDropdown: undefined,
  setShowDropdown: undefined,
  checkboxDataCy: null,
  renderColumnName: null,
  filterColumns: null,
};

export default ColumnsDropdown;
